<script setup lang="ts">
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import type { IDailyTemperaturePeriodFilter } from '@/interfaces/entities/daily-temperature'
import type { IShift } from '@/interfaces/entities/shifts'
import {
  FIELD_INPUT_CLASS,
  PERIOD_SELECT_FULL_DAY,
  PERIOD_SELECT_MANUAL,
  applyPeriodSelectValue,
  isCustomHourEnabled,
  periodSelectValue,
} from '@/utils/daily-temperature/period-filter'
import { useI18n } from '@/composable/useI18n'
import { onClickOutside } from '@vueuse/core'
import { computed, ref } from 'vue'

interface IPeriodOption {
  id: string
  name: string
}

const props = defineProps<{
  periodFilter: IDailyTemperaturePeriodFilter
  shifts: IShift[]
  hasShifts: boolean
  compact?: boolean
}>()

const emit = defineEmits<{
  'update:periodFilter': [value: IDailyTemperaturePeriodFilter]
}>()

const { t } = useI18n()

const rootRef = ref<HTMLElement | null>(null)
const isPeriodDropdownOpen = ref(false)

const fieldGrowClass = computed(() =>
  props.compact ? 'min-w-0 flex-1 basis-0' : 'min-w-0 w-full',
)

const isManualPeriod = computed(() => isCustomHourEnabled(props.periodFilter))

const selectedPeriodId = computed(() => periodSelectValue(props.periodFilter))

const periodOptions = computed((): IPeriodOption[] => {
  const options: IPeriodOption[] = [
    { id: PERIOD_SELECT_FULL_DAY, name: t('daily_temperature.fields.full_day') },
  ]

  if (props.hasShifts) {
    for (const shift of props.shifts) {
      options.push({ id: `shift:${shift.id}`, name: shift.name })
    }
  }

  options.push({ id: PERIOD_SELECT_MANUAL, name: t('daily_temperature.fields.custom') })
  return options
})

const selectedPeriodLabel = computed(() => {
  return (
    periodOptions.value.find((option) => option.id === selectedPeriodId.value)?.name ??
    t('daily_temperature.fields.full_day')
  )
})

onClickOutside(rootRef, () => {
  isPeriodDropdownOpen.value = false
})

function togglePeriodDropdown() {
  isPeriodDropdownOpen.value = !isPeriodDropdownOpen.value
}

function selectPeriod(option: IPeriodOption) {
  emit('update:periodFilter', applyPeriodSelectValue(option.id, props.periodFilter))
  isPeriodDropdownOpen.value = false
}

function updatePeriodFilter(patch: Partial<IDailyTemperaturePeriodFilter>) {
  emit('update:periodFilter', { ...props.periodFilter, ...patch })
}
</script>

<template>
  <SearchBarField :label="t('daily_temperature.fields.period')" :class="fieldGrowClass">
    <div
      ref="rootRef"
      class="relative w-full"
      :class="isPeriodDropdownOpen ? 'z-50' : 'z-10'">
      <button
        type="button"
        :class="[FIELD_INPUT_CLASS, 'flex items-center justify-between gap-2 text-left']"
        :aria-expanded="isPeriodDropdownOpen"
        :aria-label="t('daily_temperature.fields.period')"
        @click="togglePeriodDropdown">
        <span class="min-w-0 truncate">{{ selectedPeriodLabel }}</span>
        <svg
          class="h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-200"
          :class="isPeriodDropdownOpen ? 'rotate-180' : 'rotate-0'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        v-show="isPeriodDropdownOpen"
        class="absolute left-0 top-full z-50 mt-1 max-h-96 w-full overflow-y-auto rounded-none border border-zinc-200 bg-white shadow-lg">
        <div
          v-for="option in periodOptions"
          :key="option.id"
          class="cursor-pointer border-b border-stroke px-3 py-2 font-medium transition-colors duration-300 hover:bg-lightGray"
          @click="selectPeriod(option)">
          {{ option.name }}
        </div>
      </div>
    </div>
  </SearchBarField>

  <SearchBarField
    v-if="isManualPeriod"
    :label="t('daily_temperature.fields.start_hour')"
    :class="fieldGrowClass">
    <input
      :value="periodFilter.startHour"
      type="time"
      :class="FIELD_INPUT_CLASS"
      @input="updatePeriodFilter({ startHour: ($event.target as HTMLInputElement).value })" />
  </SearchBarField>

  <SearchBarField
    v-if="isManualPeriod"
    :label="t('daily_temperature.fields.end_hour')"
    :class="fieldGrowClass">
    <input
      :value="periodFilter.endHour"
      type="time"
      :class="FIELD_INPUT_CLASS"
      @input="updatePeriodFilter({ endHour: ($event.target as HTMLInputElement).value })" />
  </SearchBarField>
</template>
